import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, UserCheck, Ticket as TicketIcon, DollarSign, Download, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { formatZAR } from '../lib/format';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="card-embossed p-6">
    <div className="flex items-center gap-2 text-[#C0C0C0]">
      <Icon size={18} />
      <span className="text-xs uppercase tracking-[0.2em]">{label}</span>
    </div>
    <p data-testid={testid} className="mt-3 font-serif-display text-4xl text-gold-gradient font-black">
      {value}
    </p>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total_users: 0, verified_users: 0, tickets_this_week: 0, revenue_this_week: 0 });
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/admin/stats'), api.get('/admin/users')])
      .then(([s, u]) => {
        setStats(s.data || {});
        setUsers(u.data?.users || []);
      })
      .catch(() => {});
  }, []);

  const exportDb = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/db-export', { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename =
        (match && match[1]) ||
        `goldenfish-db-export-${new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 15)}.zip`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success('Database exported');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [u.first_name, u.surname, u.email, u.mobile].some((x) => String(x || '').toLowerCase().includes(s));
  });

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Admin Control</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-black text-gold-gradient">Dashboard</h1>
        </div>
        <button
          data-testid="admin-export-db"
          onClick={exportDb}
          disabled={exporting}
          className="btn-ghost-gold inline-flex items-center gap-2 !py-2.5 !px-5 text-sm disabled:opacity-50"
        >
          {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
          {exporting ? 'Exporting…' : 'Export Database'}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat icon={Users} label="Total users" value={stats.total_users || 0} testid="stat-total-users" />
        <Stat icon={UserCheck} label="Verified" value={stats.verified_users || 0} testid="stat-verified" />
        <Stat icon={TicketIcon} label="Tickets this week" value={stats.tickets_this_week || 0} testid="stat-tickets-week" />
        <Stat icon={DollarSign} label="Revenue this week" value={formatZAR(stats.revenue_this_week || 0)} testid="stat-revenue" />
      </div>

      <div className="mt-10 card-embossed p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h3 className="font-serif-display text-xl text-white font-bold">Users</h3>
          <input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, mobile…"
            className="bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-3 py-2 text-white text-sm w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          />
        </div>

        <div className="mt-6 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#D4AF37]/15">
                <TableHead className="text-[#C0C0C0]">Name</TableHead>
                <TableHead className="text-[#C0C0C0]">Surname</TableHead>
                <TableHead className="text-[#C0C0C0]">Age</TableHead>
                <TableHead className="text-[#C0C0C0]">ID Number</TableHead>
                <TableHead className="text-[#C0C0C0]">Tel</TableHead>
                <TableHead className="text-[#C0C0C0]">Email</TableHead>
                <TableHead className="text-[#C0C0C0]">Wallet</TableHead>
                <TableHead className="text-[#C0C0C0]">Verified</TableHead>
                <TableHead className="text-[#C0C0C0]">Blocked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => (
                <TableRow
                  key={u.id}
                  data-testid={`admin-user-row-${u.id}`}
                  className="border-[#D4AF37]/10 cursor-pointer hover:bg-[#141414]"
                  onClick={() => navigate(`/admin/users/${u.id}`)}
                >
                  <TableCell className="text-white">{u.first_name}</TableCell>
                  <TableCell className="text-white">{u.surname}</TableCell>
                  <TableCell className="text-white">{u.age}</TableCell>
                  <TableCell className="text-[#C0C0C0]">{u.kyc?.id_number || '—'}</TableCell>
                  <TableCell className="text-[#C0C0C0]">{u.mobile}</TableCell>
                  <TableCell className="text-[#D4AF37]">{u.email}</TableCell>
                  <TableCell className="text-white">{formatZAR(u.wallet_balance || 0)}</TableCell>
                  <TableCell>
                    <Badge className={u.email_verified ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-[#C0C0C0]/10 text-[#C0C0C0]'}>
                      {u.email_verified ? 'Verified' : 'Pending'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={u.blocked ? 'bg-[#991B1B]/30 text-[#F87171]' : 'bg-[#0a0a0a] text-[#C0C0C0]'}>
                      {u.blocked ? 'Blocked' : 'Active'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow className="border-[#D4AF37]/10">
                  <TableCell colSpan={9} className="text-center text-[#C0C0C0] py-6">
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
